import React from 'react';
import { Form } from 'react-bootstrap';
import { WorkOrder } from '../../../types/workOrders';

/**
 * The granular sections that can be enabled when creating/editing a work order.
 * afterPictures is intentionally excluded — it is always shown (the completion-
 * evidence surface), governed by the "Kräv efterfoto" requirement, not a toggle.
 */
export interface WorkOrderSectionFlags {
    notes: boolean;
    checklists: boolean;
    beforePictures: boolean;
    progressPictures: boolean;
}

export const emptyWorkOrderSectionFlags: WorkOrderSectionFlags = {
    notes: false,
    checklists: false,
    beforePictures: false,
    progressPictures: false,
};

const SECTION_FIELDS: { key: keyof WorkOrderSectionFlags; label: string }[] = [
    { key: 'notes', label: 'Anteckningar' },
    { key: 'checklists', label: 'Checklistor' },
    { key: 'beforePictures', label: 'Före-bilder' },
    { key: 'progressPictures', label: 'Pågående-bilder' },
];

/**
 * Build the WorkOrder.enabledSections payload with an explicit boolean for every
 * toggleable section.
 *
 * It must emit `false` (not omit the key) for unchecked sections: on edit, the
 * update writes the whole `enabledSections` child node, and Firebase RTDB
 * `update()` REPLACES that node rather than deep-merging it. Returning only the
 * enabled keys — or `undefined` when none are checked (which the update layer
 * then strips) — would leave a previously-enabled section stuck on. An explicit
 * `false` guarantees that unchecking a section actually disables it.
 */
export function buildEnabledSectionsPayload(
    flags: WorkOrderSectionFlags
): NonNullable<WorkOrder['enabledSections']> {
    return {
        notes: flags.notes,
        checklists: flags.checklists,
        beforePictures: flags.beforePictures,
        progressPictures: flags.progressPictures,
    };
}

/** Map a work order's stored enabledSections back to the form flags (for edit). */
export function workOrderSectionFlagsFrom(
    enabledSections: WorkOrder['enabledSections']
): WorkOrderSectionFlags {
    return {
        notes: enabledSections?.notes === true,
        checklists: enabledSections?.checklists === true,
        beforePictures: enabledSections?.beforePictures === true,
        progressPictures: enabledSections?.progressPictures === true,
    };
}

interface WorkOrderSectionsCheckboxesProps {
    value: WorkOrderSectionFlags;
    onChange: (next: WorkOrderSectionFlags) => void;
    disabled?: boolean;
    /** Heading text rendered as an h6; defaults to "Dokumentation". Pass "" to omit it
     *  (e.g. when the caller renders its own Form.Label). */
    heading?: string;
    /** Unique prefix for the checkbox ids (avoid collisions when two forms mount). */
    idPrefix?: string;
}

/**
 * Checkboxes for enabling the granular work-order sections at create/edit time.
 * Rendered as a column beside "Krav för slutförande".
 */
export const WorkOrderSectionsCheckboxes: React.FC<WorkOrderSectionsCheckboxesProps> = ({
    value,
    onChange,
    disabled = false,
    heading = 'Dokumentation',
    idPrefix = '',
}) => (
    <>
        {heading ? <h6>{heading}</h6> : null}
        {SECTION_FIELDS.map(({ key, label }) => (
            <Form.Check
                key={key}
                type="checkbox"
                id={`${idPrefix}section-${key}`}
                label={label}
                checked={value[key]}
                disabled={disabled}
                onChange={(e) => onChange({ ...value, [key]: e.target.checked })}
                className="mb-2"
            />
        ))}
    </>
);
